/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useRef, useEffect, useCallback} from 'react'
import PropTypes from 'prop-types'

import {defineMessage, useIntl} from 'react-intl'
import {
    IconButton,
    Spacer,
    useToast as useChakraToast
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Alert from '../components/shared/ui/Alert'
import {CloseIcon} from '../components/custom-icons'

export const DEFAULT_TOAST_META = {
    svgIconTitle: defineMessage({
        id: `toast.icon.svg.title`,
        defaultMessage: `Icon on {status} toast alert`
    }),
    svgIconDescription: defineMessage({
        id: `toast.icon.svg.desc`,
        defaultMessage: `Icon on {status} toast alert`
    }),
    svgCloseTitle: defineMessage({
        id: 'toast.button.svg.close.title',
        defaultMessage: 'Close toast alert'
    }),
    svgCloseDescription: defineMessage({
        id: 'toast.button.svg.close.desc',
        defaultMessage: 'Close toast alert'
    })
}

/**
 * ToastContent is a functional component that renders the content of a toast notification.
 * It includes a close button that receives focus when the toast is displayed, enhancing accessibility
 * for keyboard users. The component supports various props to customize its appearance and behavior.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.status - The semantic state of the toast, indicating its purpose (e.g., 'success', 'error').
 * @param {string} [props.variant] - An optional variant to customize the appearance of the toast.
 * @param {string} props.title - An optional title text displayed in the toast.
 * @param {string} [props.description] - The description providing additional information about the toast.
 * @param {React.ReactNode} [props.action] - An optional action component, such as a button, to be displayed in the toast.
 * @param {Function} props.onClose - A callback function to be called when the close button is clicked.
 *
 * @returns {JSX.Element} The rendered toast content.
 */
function ToastContent({status, variant, title, description, action, onClose, formatMessage}) {
    const closeButtonRef = useRef(null)

    useEffect(() => {
        // Set focus to the close button when the toast is rendered
        if (closeButtonRef.current) {
            closeButtonRef.current.focus()
        }
    }, [])

    const handleOnClose = useCallback(() => {
        onClose()
    }, [onClose])

    return (
        <Alert
            status={status}
            variant={variant || status}
            title={title}
            description={description}
            svgIconTitle={formatMessage(DEFAULT_TOAST_META.svgIconTitle, {status})}
            svgIconDescription={formatMessage(DEFAULT_TOAST_META.svgIconDescription, {status})}
        >
            {action && (
                <>
                    <Spacer />
                    {action}
                </>
            )}
            <Spacer />
            <IconButton ref={closeButtonRef} boxSize={6} minWidth={6} onClick={handleOnClose}>
                <CloseIcon
                    boxSize={4}
                    aria-label={formatMessage(DEFAULT_TOAST_META.svgCloseTitle)}
                    svgTitle={formatMessage(DEFAULT_TOAST_META.svgCloseTitle)}
                    svgDescription={formatMessage(DEFAULT_TOAST_META.svgCloseDescription)}
                />
            </IconButton>
        </Alert>
    )
}

ToastContent.propTypes = {
    status: PropTypes.string.isRequired,
    variant: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    action: PropTypes.node,
    onClose: PropTypes.func.isRequired,
    formatMessage: PropTypes.func.isRequired
}

/**
 * Display a toast message on the screen.
 * This is a custom hook to handle showing toasts in the app, preventing duplicate toasts and to add action elements
 * to toasts when required. It supports all props supported by Chakra toast.
 *
 * By default, the toast does not automatically dismiss, allowing users to interact with it at their convenience.
 * When the toast is opened, focus is automatically set to the close button to enhance accessibility for keyboard users.
 * Once the toast is closed, focus is returned to the element that was focused before the toast appeared, if it still exists in the DOM.
 *
 * @param {string} title Message text to be displayed in toast
 * @param {string} id - id provided to the toast to avoid duplicate toast ids, use it if multiple toasts are needed
 * @param {string} status Semantic state of the toast - success | error | info | warning
 * @param {node} action Optional component to be displayed in the toast (eg. Button to allow user to perform action)
 * @param {string} position The placement of the toast on screen
 * @param {number} duration The delay before the toast hides (in milliseconds)
 */
export function useToast() {
    const toast = useChakraToast()
    const {formatMessage} = useIntl()
    const previousFocusRef = useRef(null)

    return ({
        title,
        description,
        status = 'info',
        action,
        position = 'top-right',
        duration = null, // toast do not automatically disappear
        variant,
        isClosable = true
    }) => {
        // Capture the currently focused element
        previousFocusRef.current = document.activeElement

        let toastConfig = {
            title,
            status,
            isClosable,
            position,
            duration,
            variant,
            render: ({onClose}) => (
                <ToastContent
                    status={status}
                    variant={variant}
                    title={title}
                    description={description}
                    action={action}
                    onClose={onClose}
                    formatMessage={formatMessage} // pass as Chakra Toast doesn't access to IntlProvider context
                />
            ),
            onCloseComplete: () => {
                // Return focus to the previous element if it still exists in DOM when the toast is closed
                if (previousFocusRef.current) {
                    previousFocusRef.current.focus()
                }
            }
        }

        toast(toastConfig)
    }
}
